/**
 * Authentication Store
 * Zustand store for authentication state management
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@/types/api.types';
import { getStorageAdapter } from '@/services/platform';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User) => void;
  logout: () => void;
  clearError: () => void;
}

export type AuthStore = AuthState & AuthActions;

// Pre-read persisted auth state synchronously before store creation.
// Zustand 5's persist middleware uses an internal thenable chain that can
// silently fail, leaving hydration callbacks never called. We bypass this
// by reading localStorage directly and initializing the store with the
// correct state — no async hydration needed.
function getPersistedAuth(): { user: User | null; isAuthenticated: boolean } {
  try {
    const raw = getStorageAdapter().getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.state?.user && parsed.state.isAuthenticated) {
        return { user: parsed.state.user, isAuthenticated: true };
      }
    }
  } catch {
    // Corrupted storage — ignore
  }
  return { user: null, isAuthenticated: false };
}

const persistedAuth = getPersistedAuth();

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // Initial state — pre-hydrated from localStorage
      user: persistedAuth.user,
      isAuthenticated: persistedAuth.isAuthenticated,
      isLoading: false,
      error: null,
      _hasHydrated: true, // Always true — we hydrated synchronously above

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setLoading: (isLoading) =>
        set({
          isLoading,
        }),

      setError: (error) =>
        set({
          error,
        }),

      login: (user) =>
        set({
          user,
          isAuthenticated: true,
          error: null,
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        }),

      clearError: () =>
        set({
          error: null,
        }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => getStorageAdapter()),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
